import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import DismissibleNotice from '../../components/dismissibleMessage/DismisibleNotice.tsx';
import { useHeaderVisibility } from '../../components/header/context/HeaderVisibilityContext.tsx';
import Header from '../../components/header/Header.tsx';
import Loading from '../../components/loading/Loading.tsx';
import ConfirmModal from '../../components/modal/ConfirmModal.tsx';
import Select from '../../components/select/Select.tsx';
import { useUserContext } from '../../user/context/UserContext.tsx';
import { HOST, ROUTE_NAME_OPTIONS } from '../../utils/constants.ts';
import { isLogged } from '../../utils/utils.ts';
import { CartItem, useProductContext } from '../context/ProductContext.tsx';
import {
  Product,
  ProductOptionValues,
  productTypeOptions,
} from '../ProductType.ts';
import { getWeightLabel } from '../utils.ts';
import './product-list.scss';
import ProductRow from './ProductRow.tsx';

const ProductList = () => {
  const {
    setSearchProduct,
    searchProduct,
    filteredProducts,
    addToCart,
    setProducts,
    productTypeFilter,
    setProductTypeFilter,
  } = useProductContext();
  const { userInfo } = useUserContext();
  const [cartItems, setCartItems] = useState<{
    [key: string]: CartItem;
  }>({});

  // Convertir las opciones de tipo de producto al formato de react-select
  const typeFilterOptions = [
    { value: '', label: 'Todos los productos' },
    ...productTypeOptions.map((option) => ({
      value: option.value,
      label: option.label,
    })),
  ];

  // Estado para el valor seleccionado del filtro
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<{
    value: string;
    label: string;
  } | null>(typeFilterOptions[0]);

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 20;
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const topRef = useRef<HTMLDivElement | null>(null);

  // Función para manejar el cambio de filtro por tipo
  const handleTypeFilterChange = (
    selectedOption: { value: string; label: string } | null
  ) => {
    setSelectedTypeFilter(selectedOption);
    setProductTypeFilter(selectedOption?.value || '');
  };

  // Estilos personalizados para react-select que coincidan con el input
  const customSelectStyles = {
    container: (provided: any) => ({
      ...provided,
      width: '100%',
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      border: '1px solid #ff9f24',
      borderRadius: '8px !important',
      boxShadow: 'inset 1px 1px 3px rgba(0, 0, 0, 0.25)',
      fontSize: '14px',
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: '300',
      lineHeight: '20px',
      letterSpacing: '0.02em',
      backgroundColor: '#fff',
      minHeight: '34px',
      height: 'auto',
      '&:hover': {
        border: '1px solid #ff9f24',
      },
      // Quitar el outline de focus que viene por defecto
      '&:focus': {
        border: '1px solid #ff9f24',
        boxShadow: 'inset 1px 1px 3px rgba(0, 0, 0, 0.25)',
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#888',
      opacity: 1,
      fontSize: '14px',
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: '300',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#000',
      fontSize: '14px',
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: '300',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#888',
      padding: '0 6px',
      '&:hover': {
        color: '#666',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '6px',
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: '10px',
      border: '1px solid #ff9f24',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontSize: '14px',
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: '300',
      backgroundColor: state.isSelected
        ? '#ff9f24'
        : state.isFocused
          ? '#fff3e0'
          : '#fff',
      color: state.isSelected ? '#fff' : '#000',
      '&:hover': {
        backgroundColor: state.isSelected ? '#ff9f24' : '#fff3e0',
      },
    }),
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const { forceShowHeader } = useHeaderVisibility();

  const navigate = useNavigate();

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchProduct, productTypeFilter]);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${HOST}/products`);

      if (!response.ok) {
        throw new Error('Error al cargar los productos');
      }

      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [setProducts]);

  useEffect(() => {
    const mainEl = document.querySelector('.main');
    if (mainEl) mainEl.scrollTop = 0;
    window.scrollTo(0, 0);
    fetchProducts();
  }, [fetchProducts]);

  const handleCartItemChange = (
    id: string,
    purchaseOption: ProductOptionValues,
    quantity: number,
    productPrice: number
  ) => {
    const product = filteredProducts.find((p) => p.id === id);
    if (!product) return;

    const weight = getWeightLabel(
      product.boxWeight,
      quantity,
      purchaseOption,
      product.productType
    );
    const partialPrice = productPrice;

    setCartItems((prev) => ({
      ...prev,
      [id]: {
        id,
        product,
        purchaseOption,
        quantity,
        weight,
        partialPrice,
        totalPrice: quantity * productPrice,
      },
    }));
  };

  const goToLogin = () => {
    navigate(`/${ROUTE_NAME_OPTIONS.LOGIN}`);
  };

  const handleAddToCart = (product: Product) => {
    if (product.hasStock === false) {
      toast.error('Este producto no tiene stock disponible', {
        duration: 3000,
      });
      return;
    }

    if (!isLogged()) {
      setIsOpenConfirmModal(true);
    } else {
      const cartItem = cartItems[product.id];

      if (
        !cartItem ||
        !cartItem.quantity ||
        isNaN(cartItem.quantity) ||
        cartItem.quantity <= 0
      ) {
        toast.error(
          `Por favor ingresa la cantidad de ${product.name} que desees`,
          { duration: 4000 }
        );
        return;
      }

      addToCart(cartItem);
      toast.custom((t) => (
        <div
          className="toast-custom"
          style={{
            background: '#fff',
            padding: '1rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            border: '1px solid #ffa726',
            position: 'relative',
            maxWidth: '90vw',
            minWidth: '320px',
          }}
        >
          <div>
            <strong>✅ {product.name}</strong> fue agregado al carrito
          </div>
          <button
            onClick={() => {
              toast.dismiss();
              navigate(`/${ROUTE_NAME_OPTIONS.SHOPPING_CART}`);
            }}
            className="primary-button"
          >
            Ir al carrito
          </button>
        </div>
      ));

      forceShowHeader();
    }
  };

  return (
    <div>
      {isLoading && <Loading />}
      {isOpenConfirmModal && (
        <ConfirmModal
          confirmText={'Ingresa'}
          bodyText={'Necesitas estar logeado para poder realizar tu pedido'}
          action={goToLogin}
          cancel={() => setIsOpenConfirmModal(false)}
        />
      )}
      <Header />
      <div className="flex-justify-between welcome-section">
        {isLogged() && (
          <div className="flex-welcome">
            <h2 className="welcome-title">Hola {userInfo?.user?.name}</h2>
          </div>
        )}
        <button
          onClick={() => {
            navigate(`/${ROUTE_NAME_OPTIONS.PRICE_LIST}`);
          }}
          className="primary-button"
        >
          Listado precios
        </button>
      </div>

      <div ref={topRef} />
      <DismissibleNotice
        id="producto-faltante"
        text="Si deseas algún producto que no está en la lista no dudes en consultarnos."
      />
      <DismissibleNotice
        id="zonas"
        text="Zonas de envío: Quilmes, Bernal, Ezpeleta, Berazategui, Wilde, Avellaneda, Sarandí. Consultar por otras zonas."
      />
      {(!isLogged() || userInfo?.user?.firstPurchase) && (
        <DismissibleNotice
          id="descuento-primera-compra"
          text="Tenés un 5% de descuento total en tu primera compra por la web"
          variant="green"
        />
      )}

      <h3 className="product-title">Listado de Frutas y Verduras</h3>
      <div className="filter-container">
        <input
          className="input flex-half"
          placeholder="busca tu producto..."
          value={searchProduct}
          onChange={(e) => setSearchProduct(e.target.value)}
        />
        <div className="flex-half">
          <Select
            value={selectedTypeFilter}
            options={typeFilterOptions}
            isSearchable={false}
            onChange={handleTypeFilterChange}
            placeholder="Filtrar por tipo..."
            styles={customSelectStyles}
          />
        </div>
      </div>
      <div className="product-list margin-top-10">
        {currentProducts && currentProducts.length > 0
          ? currentProducts.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                handleCartItemChange={handleCartItemChange}
                handleAddToCart={handleAddToCart}
              />
            ))
          : null}
      </div>
      {(!currentProducts || currentProducts.length === 0) && (
        <p className="no-products-message">
          No hay resultados para tu búsqueda
        </p>
      )}
      <div className="pagination">
        {filteredProducts.length > productsPerPage && (
          <div className="page-numbers">
            {Array.from(
              { length: Math.ceil(filteredProducts.length / productsPerPage) },
              (_, index) => (
                <button
                  key={index + 1}
                  className={`page-number ${
                    currentPage === index + 1 ? 'active' : ''
                  }`}
                  onClick={() => paginate(index + 1)}
                >
                  {index + 1}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;
